import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Link, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import jMarkBlack from "../../styles/images/Jacobs_jmark_cmyk_richblack.png";
import jMarkWhite from "../../styles/images/Jacobs_jmark_rgb_white.png";

import * as ParameterUtilities from "../../utilities/ParameterUtilities";
import {
    persistExpandedRoutes
} from "../../redux/persist/persistActions";
import {appSetLeftDrawerOpen} from "../../redux/app/appActions";

import {
	styled,
	useTheme,
	Drawer,
	Box,
	AppBar,
	CssBaseline,
	Toolbar,
	IconButton,
	List,
	Typography,
	Divider,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
} from '@mui/material';

const PREFIX = 'NavigationDrawer';

const classes = {
    drawer: `${PREFIX}-drawer`,
    drawerPaper: `${PREFIX}-drawerPaper`,
    toolbar: `${PREFIX}-toolbar`,
    drawerHeader: `${PREFIX}-drawerHeader`,
    list: `${PREFIX}-list`,
    listItem: `${PREFIX}-listItem`,
    subListItem: `${PREFIX}-subListItem`,
    centerJMark: `${PREFIX}-centerJMark`,
    jMark: `${PREFIX}-jMark`
};

const StyledDrawer = styled(Drawer)((
    {
        theme
    }
) => ({
    [`&.${classes.drawer}`]: {
        width: drawerWidth,
        flexShrink: 0,
        overflow: 'hidden',
        boxShadow: 'rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px'
    },

    [`& .${classes.drawerPaper}`]: {
        width: drawerWidth,
    },

    [`& .${classes.toolbar}`]: theme.mixins.toolbar,

    [`& .${classes.drawerHeader}`]: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
        justifyContent: 'flex-end',
    },

    [`& .${classes.list}`]: {
        padding: '0px',
        height: 'calc(100% - 165px)',
        overflow: 'auto'
    },

    [`& .${classes.listItem}`]: {
        "&.Mui-selected": {
            backgroundColor: theme.palette.action.selected
        },
        "&.Mui-selected:hover": {
            backgroundColor: theme.palette.action.selected
        }
    },

    [`& .${classes.subListItem}`]: {
        paddingLeft: '36px',
        "&.Mui-selected": {
            backgroundColor: theme.palette.action.selected
        },
        "&.Mui-selected:hover": {
            backgroundColor: theme.palette.action.selected
        }
    },

    [`& .${classes.centerJMark}`]: {
        position: 'absolute',
        bottom: '10px',
        width: '100%',
        // height: '95px',
        // backgroundColor: 'rgba(0,0,0,0.08)'
        borderTop: 'solid 1px darkgrey'
    },

    [`& .${classes.jMark}`]: {
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        height: '75px',
        marginTop: '10px'
    }
}));

const drawerWidth = 240;

export default function NavigationDrawer() {

    const dispatch = useDispatch();

    const { t, i18n } = useTranslation();

    // App State
    const app = useSelector(state => state.app);
    const persist = useSelector(state => state.persist);
    const expandedRoutes = useSelector(state => state.persist.expandedRoutes);

    // Component State
    const [displayRoutes, setDisplayRoutes] = useState([]);
    const location = useLocation();
    const pathname = location.pathname;
    const routes = app.routes;
    let { id, tab } = useParams();

    const isSelected = (route) => {

        let trimmedRoute = ParameterUtilities.trimParams(route);

        if (pathname === trimmedRoute) { // If URL equals basic route
            return true;
        } else if ((pathname) === (trimmedRoute + '/' + id)) { // If URL equals basic route plus ID param
            return true;
        } else if ((pathname) === (trimmedRoute + '/' + id + '/' + tab)) { // If URL equals basic route plus ID and TAB params
            return true;
		} else if ((pathname) === (trimmedRoute + '/' + (id !== undefined ? (id + '/') : '') + tab)) { // If URL equals basic route plus ID and TAB params
			return true;
        } else {
            return false;
        }
    };

    const handleClick = (e, label) => {
        // Prevent default NavLink behavior
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Update the collapsed state
        let newExpandedMenus = [];
        newExpandedMenus = newExpandedMenus.concat(expandedRoutes);
        if (newExpandedMenus.findIndex(m => m.toLowerCase() === label.toLowerCase()) >= 0) {
            newExpandedMenus.splice(newExpandedMenus.findIndex(m => m.toLowerCase() === label.toLowerCase()), 1);
        } else {
            newExpandedMenus.push(label);
        }

        dispatch(persistExpandedRoutes(newExpandedMenus));
    };

    const openLeftDrawer = e => {
        dispatch(appSetLeftDrawerOpen(true));
    };

    const closeLeftDrawer = e => {
        dispatch(appSetLeftDrawerOpen(false));
    };

    useEffect(() => {
        let parsedPath = pathname;
        if (id) {
            parsedPath = parsedPath.substring(0, parsedPath.indexOf(id));
        }

        if (parsedPath.substr(parsedPath.length - 1) === '/') {
            parsedPath = parsedPath.substr(0, parsedPath.length - 1);
        }

        // Find the route
        let route = null;
        for (let i = 0; i < routes.length; i++) {
            const r = routes[i];
            let toCompare = r.route;
            if (r.route.indexOf(":") >= 0) {
                toCompare = r.route.substring(0, r.route.indexOf("/:"));
            }

            if (toCompare.toLowerCase() === parsedPath.toLowerCase()) {
                route = r;
                break;
            }
        }

        if (route) {
            // Expand the route or its parent
            if (route.navParent === '') {
                // If not expanded then expand
                if (expandedRoutes.indexOf(route.label) < 0) {
                    handleClick(null, route.label);
                }
            }
            else {
                // If not expanded then expand
                if (expandedRoutes.indexOf(route.navParent) < 0) {
                    handleClick(null, route.navParent);
                }
            }
        }
    }, [pathname]);

    useEffect(() => {
        if (routes && routes.length > 0) {
            const newRoutes = [];
            for (let route of routes) {
                const children = routes.find(r => r.navParent === route.label);
                if (children) {
                    route.collapsible = true;
                } else {
                    route.collapsible = false;
                }
                newRoutes.push(route);
            }
            setDisplayRoutes(newRoutes);
        }
    }, [routes]);

    return (
        <StyledDrawer
            className={classes.drawer}
            variant="persistent"
            BackdropProps={{ invisible: false }}
            classes={{
                paper: classes.drawerPaper,
            }}
            open={app.leftDrawerOpen}
            // onMouseEnter={openLeftDrawer}
            // onMouseOver={openDrawer} // Child events will bubble up
            // onMouseLeave={closeLeftDrawer}
        >
            <div className={classes.toolbar} />
            <Divider />
            <List className={classes.list}>
                {displayRoutes.map((r, i) => {
                    if (r.navDrawer && (r.navParent === '')) {
                        return (
                            <div key={r.label}>
                                    <ListItemButton selected={isSelected(r.route)} className={classes.listItem} key={r.label} component={NavLink} to={ParameterUtilities.trimParams(r.route)} >
                                        <ListItemIcon>{r.icon}</ListItemIcon>
                                        <ListItemText primary={t(r.label)} />
                                        {r.collapsible === true &&
                                            <React.Fragment>
                                                {
                                                    expandedRoutes.indexOf(r.label) >= 0 ? <ExpandLess onClick={(e) => {
                                                        handleClick(e, r.label)
                                                    }}/> : <ExpandMore onClick={(e) => {
                                                        handleClick(e, r.label)
                                                    }}/>
                                                }
                                            </React.Fragment>
                                        }
                                    </ListItemButton>
                                {/*https://dev.to/jsmanifest/create-a-modern-dynamic-sidebar-menu-in-react-using-recursion-36eo*/}
                                {displayRoutes.map((p, i) => {
                                    if (p.navDrawer && (p.navParent == r.label)) {
                                        return (
                                            <Collapse key={i} in={expandedRoutes.indexOf(r.label) >= 0} timeout="auto" unmountOnExit>
                                                <ListItemButton selected={isSelected(p.route)} className={classes.subListItem} key={p.label} component={NavLink} to={ParameterUtilities.trimParams(p.route)} >
                                                    <ListItemIcon>{p.icon}</ListItemIcon>
                                                    <ListItemText primary={t(p.label)} />
                                                </ListItemButton>
                                            </Collapse>
                                        )
                                    } else {
                                        return null;
                                    }
                                })}
                            </div>
                        );
                    } else {
                        return null;
                    }
                })}
            </List>
            <div className={classes.centerJMark}>
                {persist.themeLight &&
                    <img src={jMarkBlack} alt="Jacobs" className={classes.jMark} />
                }
                {!persist.themeLight &&
                    <img src={jMarkWhite} alt="Jacobs" className={classes.jMark} />
                }
            </div>
        </StyledDrawer>
    );
}
